import React, { useEffect, useMemo, useRef, useState } from "react";

function formatDisplayDate(iso) {
  const [year, month, day] = String(iso || "").split("-");
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(date.getTime())) return iso || "";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function parseIso(iso) {
  const [year, month, day] = String(iso || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  return { year, month, day };
}

function toIso(year, month, day) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function monthLabel(year, month) {
  return new Date(year, month - 1, 1).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
}

export default function MatkaDateSelect({ id, value, onChange }) {
  const selected = parseIso(value);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => ({
    year: selected?.year || new Date().getFullYear(),
    month: selected?.month || new Date().getMonth() + 1,
  }));
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const cells = useMemo(() => {
    const firstWeekday = new Date(view.year, view.month - 1, 1).getDay();
    const daysInMonth = new Date(view.year, view.month, 0).getDate();
    const days = Array.from({ length: firstWeekday }, () => null);
    for (let day = 1; day <= daysInMonth; day += 1) days.push(day);
    return days;
  }, [view]);

  const shiftMonth = (amount) => {
    const next = new Date(view.year, view.month - 1 + amount, 1);
    setView({ year: next.getFullYear(), month: next.getMonth() + 1 });
  };

  const openCalendar = () => {
    if (selected) setView({ year: selected.year, month: selected.month });
    setOpen((current) => !current);
  };

  const selectDay = (day) => {
    onChange(toIso(view.year, view.month, day));
    setOpen(false);
  };

  return (
    <div className="matka-date-select" ref={rootRef}>
      <button
        id={id}
        type="button"
        className="matka-date-select-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={openCalendar}
      >
        <span>{formatDisplayDate(value)}</span>
        <i className="bi bi-calendar3" aria-hidden="true"></i>
      </button>

      {open ? (
        <div className="matka-date-calendar" role="dialog" aria-label="Choose a date">
          <div className="matka-date-calendar-head">
            <button type="button" aria-label="Previous month" onClick={() => shiftMonth(-1)}>
              <i className="bi bi-chevron-left" aria-hidden="true"></i>
            </button>
            <span>{monthLabel(view.year, view.month)}</span>
            <button type="button" aria-label="Next month" onClick={() => shiftMonth(1)}>
              <i className="bi bi-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
          <div className="matka-date-weekdays">
            {WEEKDAYS.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>
          <div className="matka-date-grid">
            {cells.map((day, index) => {
              if (!day) return <span key={`empty-${index}`} />;
              const iso = toIso(view.year, view.month, day);
              const isSelected = iso === value;
              return (
                <button
                  key={iso}
                  type="button"
                  className={isSelected ? "is-selected" : ""}
                  onClick={() => selectDay(day)}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
